import React, { useState, useEffect } from "react";
import { Input, Button, Space, Table, Modal, Form, Select } from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import axios from "axios";
import { URLS } from "../constants/baseURLS";

const { TextArea } = Input;
const { Option } = Select;

interface User {
  key: number;
  name: string;
  position: string;
  group?: string;
  about?: string;
  image?: any;
  _id?: string;
  deleted?: boolean;
  // Backend uses `published`; keep isPublished optional for legacy data
  published?: boolean;
  isPublished?: boolean;
  // NEW: distinguishes Success Story vs Corporate Testimonial entries
  type?: "SUCCESS_STORY" | "CORPORATE_TESTIMONIAL";
  userPostDataInfo?: Array<{
    name: string;
    role: string;
    emailId: string;
    createdAt: string;
    updatedAt: string;
  }>;
}

const initialUsers: User[] = [
  { key: 1, name: "John", position: "Developer" },
  { key: 2, name: "Alice", position: "Designer" },
];

const Success = () => {
  const [form] = Form.useForm();
  const [users, setUsers] = useState<User[]>(initialUsers);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editUserIndex, setEditUserIndex] = useState<number | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [successData, setSuccessData] = useState<User[]>([]);
  // Publish/unpublish filter for admin
  const [publishFilter, setPublishFilter] = useState<'all' | 'published' | 'unpublished'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5); // Set the default page size

  // Get user role from localStorage
  const getUserRole = (): string => {
    const userDetailsString = localStorage.getItem('userDetails');
    const userDetails = userDetailsString ? JSON.parse(userDetailsString) : null;
    return userDetails?.role || '';
  };
  const userRole = getUserRole();

  const handleFileUpload = async (event: any) => {
    const file = event?.target?.files?.[0];
    if (!file || file.size > 5 * 1024 * 1024) {
      alert(file ? "File size exceeds 5MB" : "No file selected");
      return;
    }

    const formDataFile = new FormData();
    formDataFile.append("file", file);

    try {
      const response = await axios.post(`https://service.ireedindia.com/upload`, formDataFile);
      setFileName(response.data.filename);
    } catch (error) {
      console.error("File upload failed:", error);
    }
  };

  // FIXED: was updating the unused `users` dummy state (so the table never
  // reflected an edit). Now updates `successData`, which is what's actually
  // rendered, and merges the response with the existing record so fields the
  // API doesn't echo back aren't lost.
  const handleUpdate = async (userId: string, updatedData: Partial<User>) => {
    try {
      const response = await axios.patch(`https://service.ireedindia.com/v1/sucess-story/${userId}`, updatedData);

      // Don't assume response.data IS the updated document. Some APIs wrap it
      // as { data: {...} }, others just return { success, message } with no
      // doc at all — in that case the old code silently merged nothing and
      // the table looked unchanged even though the PATCH succeeded.
      const serverUser = response.data?.data ?? response.data ?? {};

      const updatedList = successData.map((user) =>
        user._id === userId
          ? { ...user, ...updatedData, ...serverUser } // updatedData = the values we just sent (known-correct), serverUser layered on top if it's a real doc
          : user
      );
      setSuccessData(updatedList);
    } catch (error) {
      console.error("Error updating success story:", error);
      alert("Failed to update. Please try again.");
    }
  };

  const onFinish = async (values: any) => {
    // Get userId from localStorage
    const userDetailsString = localStorage.getItem('userDetails');
    const userDetails = userDetailsString ? JSON.parse(userDetailsString) : null;
    const userId = userDetails?.userId;

    const newUser: User = {
      key: users.length + 1,
      name: values.name,
      position: values.position,
      group: values.group,
      about: values.about,
      image: fileName,
      type: values.type, // NEW
    };

    if (editUserIndex !== null) {
      await handleUpdate(editUserIndex.toString(), {
        ...values,
        image: fileName,
        type: values.type, // NEW
        userId: userId,
      });
      form.resetFields();
      setIsModalVisible(false);
      setEditUserIndex(null);
    } else {
      try {
        const response = await axios.post(`https://service.ireedindia.com/v1/create-suceess-story`, { ...newUser, userId: userId });
        setSuccessData([...successData, response.data]);
        form.resetFields();
        setIsModalVisible(false);
        setEditUserIndex(null);
      } catch (error) {
        console.error("Error adding user:", error);
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get role and userId from localStorage
        const userDetailsString = localStorage.getItem('userDetails');
        const userDetails = userDetailsString ? JSON.parse(userDetailsString) : null;
        const role = userDetails?.role;
        const userId = userDetails?.userId;

        let url = `${URLS.DEV}v1/find-sucees-story`;

        // If not admin, add userId as param
        if (role !== 'admin' && userId) {
          url += `?userId=${encodeURIComponent(userId)}`;
        }

        const response = await fetch(url);
        const responseData = await response.json();
        const filteredData = responseData.data.filter((user: User) => !user.deleted);
        setSuccessData(filteredData);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const handleEdit = (record: any) => {
    form.setFieldsValue({
      name: record.name,
      position: record.position,
      group: record.group,
      about: record.about,
      type: record.type || "SUCCESS_STORY", // NEW
    });
    setFileName(record.image); // Set the existing image
    setIsModalVisible(true);
    setEditUserIndex(record._id);
  };

  const handleDelete = async (userId: any) => {
    try {
      await axios.delete(`https://service.ireedindia.com/v1/sucess-story/${userId}`);
      const updatedUsers = successData.filter((user) => user._id !== userId);
      setSuccessData(updatedUsers);
    } catch (error) {
      console.error("Error deleting user:", error);
    }
  };

  // Publish/unpublish functionality
  const handlePublish = async (userId: string, currentStatus: boolean) => {
    try {
      const newStatus = !currentStatus;
      console.log('Toggling publish status:', { userId, currentStatus, newStatus });

      const response = await axios.patch(`https://service.ireedindia.com/v1/sucess-story/${userId}`, {
        published: newStatus
      });

      console.log('Response from server:', response.data);

      // Update local state with the new status
      const updatedData = successData.map((user) =>
        user._id === userId ? { ...user, published: newStatus, isPublished: newStatus } : user
      );
      setSuccessData(updatedData);

      // Optionally refetch data to ensure sync with backend
      // You can uncomment this to always fetch fresh data after update
      // fetchData();
    } catch (error) {
      console.error('Error updating publish status:', error);
      alert('Failed to update publish status. Please try again.');
    }
  };

  const successColumns = [
    { title: "Name", dataIndex: "name", key: "name", width: 150 },
    { title: "Position", dataIndex: "position", key: "position", width: 150 },
    { title: "Group", dataIndex: "group", key: "group", width: 120 },
    { title: "About", dataIndex: "about", key: "about", width: 200, ellipsis: true },
    // NEW: shows which bucket the entry belongs to
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 150,
      render: (type: User["type"]) => (
        <span
          style={{
            padding: '4px 10px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 600,
            backgroundColor: type === "CORPORATE_TESTIMONIAL" ? '#e0e7ff' : '#fce7f3',
            color: type === "CORPORATE_TESTIMONIAL" ? '#3730a3' : '#9d174d',
          }}
        >
          {type === "CORPORATE_TESTIMONIAL" ? "Corporate Testimonial" : "Success Story"}
        </span>
      ),
    },
    {
      title: "Image",
      dataIndex: "image",
      key: "image",
      width: 80,
      render: (image: string) => (
        image ? (
          <img
            src={`https://service.ireedindia.com/image/${image}`}
            alt="User"
            style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
          />
        ) : (
          <span style={{ color: '#999' }}>No Image</span>
        )
      ),
    },
    {
      title: "Status",
      dataIndex: "published",
      key: "published",
      width: 125,
      render: (published: boolean, record: User) => {
        const isPub = published ?? record.isPublished ?? false;
        return (
          <span style={{
            padding: '4px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '600',
            backgroundColor: isPub ? '#d1fae5' : '#fef3c7',
            color: isPub ? '#065f46' : '#92400e'
          }}>
            {isPub ? '✓ Published' : 'Draft'}
          </span>
        );
      },
    },
    // Admin-only columns
    ...(userRole === 'admin' ? [
      {
        title: "Created By",
        dataIndex: "userPostDataInfo",
        key: "createdBy",
        width: 150,
        render: (userPostDataInfo: any) => {
          if (!userPostDataInfo || userPostDataInfo.length === 0) return 'N/A';
          const user = userPostDataInfo[0];
          return (
            <div style={{ fontSize: '12px' }}>
              <div style={{ fontWeight: '600', color: '#1f2937' }}>{user.name}</div>
              <div style={{ color: '#6b7280' }}>{user.emailId}</div>
              <div style={{ color: '#1890ff', fontSize: '11px' }}>{user.role}</div>
            </div>
          );
        },
      },
      {
        title: "Created At",
        dataIndex: "userPostDataInfo",
        key: "createdAt",
        width: 120,
        render: (userPostDataInfo: any) => {
          if (!userPostDataInfo || userPostDataInfo.length === 0) return 'N/A';
          const user = userPostDataInfo[0];
          const date = new Date(user.createdAt);
          return (
            <div style={{ fontSize: '12px', color: '#4b5563' }}>
              <div>{date.toLocaleDateString()}</div>
              <div style={{ color: '#9ca3af' }}>{date.toLocaleTimeString()}</div>
            </div>
          );
        },
      },
      {
        title: "Updated At",
        dataIndex: "userPostDataInfo",
        key: "updatedAt",
        width: 120,
        render: (userPostDataInfo: any) => {
          if (!userPostDataInfo || userPostDataInfo.length === 0) return 'N/A';
          const user = userPostDataInfo[0];
          const date = new Date(user.updatedAt);
          return (
            <div style={{ fontSize: '12px', color: '#4b5563' }}>
              <div>{date.toLocaleDateString()}</div>
              <div style={{ color: '#9ca3af' }}>{date.toLocaleTimeString()}</div>
            </div>
          );
        },
      },
    ] : []),
    {
      title: "Actions",
      key: "actions",
      width: 180,
      fixed: "right" as const,
      render: (_: any, record: any) => {
        const currentPublishStatus = record.published ?? record.isPublished ?? false;
        return (
          <Space size="middle">
            <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
            {userRole === 'admin' && (
              <Button
                type={currentPublishStatus ? "default" : "primary"}
                onClick={() => handlePublish(record._id, currentPublishStatus)}
                style={{ backgroundColor: currentPublishStatus ? '#faad14' : '#52c41a', color: '#fff', borderColor: 'transparent', fontSize: '12px', padding: '4px 8px' }}
              >
                {currentPublishStatus ? 'U' : 'P'}
              </Button>
            )}
            {userRole === 'admin' && (
              <Button icon={<DeleteOutlined />} onClick={() => handleDelete(record._id)} danger />
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <h2>Success Data</h2>
      <Button
        type="primary"
        onClick={() => {
          setIsModalVisible(true);
          setFileName(null);
          form.resetFields();
        }}
      >
        Add User
      </Button>
      {/* Publish/Unpublish Filter for Admin */}
      {userRole === 'admin' && (
        <div style={{ margin: '16px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontWeight: 500, fontSize: 15 }}>Filter:</span>
          <select
            value={publishFilter}
            onChange={e => setPublishFilter(e.target.value as 'all' | 'published' | 'unpublished')}
            style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 15 }}
          >
            <option value="all">All</option>
            <option value="published">Published</option>
            <option value="unpublished">Unpublished</option>
          </select>
        </div>
      )}
      <Table
        columns={successColumns}
        dataSource={
          userRole === 'admin'
            ? (publishFilter === 'all'
                ? successData
                : successData.filter(user => {
                    const isPub = user.published ?? user.isPublished ?? false;
                    return publishFilter === 'published' ? isPub : !isPub;
                  })
              )
            : successData
        }
        scroll={{ x: 1400 }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          onChange: (page, pageSize) => {
            setCurrentPage(page);
            setPageSize(pageSize);
          },
          showSizeChanger: true,
          pageSizeOptions: ["5", "10", "20"],
        }}
      />
      <Modal
        title={editUserIndex !== null ? "Edit User" : "Add User"}
        visible={isModalVisible}
        onCancel={() => {
          form.resetFields();
          setIsModalVisible(false);
          setEditUserIndex(null);
          setFileName(null);
        }}
        footer={null}
      >
        <Form form={form} name="users" onFinish={onFinish} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
          <Form.Item label="Name" name="name" rules={[{ required: true, message: "Please input the user name!" }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Position" name="position" rules={[{ required: true, message: "Please input the user position!" }]}>
            <Input />
          </Form.Item>
          {/* NEW: type selector */}
          <Form.Item
            label="Type"
            name="type"
            initialValue="SUCCESS_STORY"
            rules={[{ required: true, message: "Please select a type!" }]}
          >
            <Select>
              <Option value="SUCCESS_STORY">Success Story</Option>
              <Option value="CORPORATE_TESTIMONIAL">Corporate Testimonial</Option>
            </Select>
          </Form.Item>
          <Form.Item label="Group" name="group">
            <TextArea />
          </Form.Item>
          <Form.Item label="About" name="about">
            <TextArea />
          </Form.Item>
          <Form.Item label="Image" name="image">
            <div>
              <input
                type="file"
                onChange={handleFileUpload}
                accept="image/png,image/jpg,image/jpeg,image/webp"
                className="form-control-file"
              />
              {fileName && (
                <div style={{ marginTop: '10px' }}>
                  <img
                    src={`https://service.ireedindia.com/image/${fileName}`}
                    alt="Preview"
                    style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: 4 }}
                  />
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>Current Image</p>
                </div>
              )}
            </div>
          </Form.Item>
          <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
            <Button type="primary" htmlType="submit">
              {editUserIndex !== null ? "Update" : "Add"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Success;